import { useEffect, useState } from "react";
import Slider from "react-slick";
import Lottie from "lottie-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import styles from "../../styles/containers/home/about.module.css";

// Reuse the same Lottie set + colors from the BusinessType selection
// screen so this welcome banner stays visually consistent with the
// onboarding step 2. One representative animation + brand color per
// category — cycles through every ~2.5s to demonstrate "fits any
// business" without needing a single hero illustration.
import fitnessAnim  from "../../assets/lottie/fitness/strength.json";
import beautyAnim   from "../../assets/lottie/beauty/makeup.json";
import wellnessAnim from "../../assets/lottie/wellness/spa.json";
import businessAnim from "../../assets/lottie/business/meeting.json";
import medicalAnim  from "../../assets/lottie/medical/doctor-female.json";
import otherAnim    from "../../assets/lottie/other/dog.json";

const SHOWCASE = [
  { id: "fitness",  name: "כושר ותנועה",     anim: fitnessAnim,  gradient: "linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)" },
  { id: "beauty",   name: "טיפוח ויופי",     anim: beautyAnim,   gradient: "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)" },
  { id: "wellness", name: "גוף ונפש",        anim: wellnessAnim, gradient: "linear-gradient(135deg, #10B981 0%, #047857 100%)" },
  { id: "business", name: "עסקים ושירותים",   anim: businessAnim, gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)" },
  { id: "medical",  name: "רפואה וקליניקות",  anim: medicalAnim,  gradient: "linear-gradient(135deg, #06B6D4 0%, #0E7490 100%)" },
  { id: "other",    name: "ועוד...",         anim: otherAnim,    gradient: "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)" },
];

const About = () => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <img src="/assets/next-icon.svg" alt="next-icon" />,
    prevArrow: <img src="/assets/prev-icon.svg" alt="prev-icon" />,
    autoplay: true,
    autoplaySpeed: 5000,
  };

  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % SHOWCASE.length);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const active = SHOWCASE[activeIdx];

  return (
    <div
      className={styles.container}
      style={{ background: active.gradient, transition: "background 1.4s ease-in-out" }}>

      <h2 className={styles.heading}>מקימים עסק בקליק תוך דקה</h2>
      <p className={styles.text}>
        מערכת ניהול תורים חכמה לכל עסק. הלקוחות שלכם קובעים תור בקלות, ואתם מנהלים הכל ממקום אחד
      </p>

      {/* Lottie showcase — only the active Lottie is mounted, so we
          don't pay for 6 simultaneous animations. The key change forces
          a fresh mount on each cycle (avoids the previous animation's
          last-frame flashing during transition). */}
      <div className={styles.showcase}>
        <div className={styles.haloRing} />
        <div className={styles.haloRing2} />
        <div
          className={styles.lottieWrap}
          key={activeIdx}
          style={active.wrapBg ? { background: active.wrapBg } : undefined}>
          <Lottie
            animationData={active.anim}
            loop
            autoplay
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>

      <div className={styles.rotatingLabel} key={`label-${activeIdx}`}>
        {active.name}
      </div>
      <div className={styles.tagline}>
        ✦ מתאים לכל סוג עסק ✦
      </div>

      <div className={styles.dots}>
        {SHOWCASE.map((_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i === activeIdx ? styles.dotActive : ""}`}
          />
        ))}
      </div>

      {/* FEATURES carousel */}
      <div className={styles.features}>
        <Slider {...settings}>
          <div className={styles.feature}>
            <img src="/assets/feature-img-1.svg" alt="feature-img-1" />
            <h3>?לאיזה עסקים השירות מתאים</h3>
            <p>
              האפליקציה שלנו מתאימה לכל בעל עסק המקבל תורים, החל מבונות
              ציפורניים, מעצבות גבות, דוגיסיטר, מאפרות, מורים פרטיים, מאמני כושר
              ועד קליניקות המקבלות מטופלים
            </p>
          </div>
          <div className={styles.feature}>
            <img src="/assets/feature-img-1.svg" alt="feature-img-1" />
            <h3>שירות אנושי זמין ומקצועי</h3>
            <p>
              אנחנו מציעים שירות לקוחות מקצועי וזמין בטלפון, וואטסאפ ומייל.
              נמצאים כאן לעזרה, לשאלות ולכל צורך שיתקום.
            </p>
          </div>
          <div className={styles.feature}>
            <img src="/assets/feature-img-1.svg" alt="feature-img-1" />
            <h3>שימור לקוחות בדרך הטובה ביותר</h3>
            <p>
              האפליקציה שולחת תזכורות ללקוחות אבל לא רק. היא תדאג לשלוח מזל טוב
              בימי הולדת, בחגים וכל הודעה שתרצו בעצם (כמו: ״אני אהיה בחופשה
              בשבוע הבא ולא אקבל תורים״)
            </p>
          </div>
        </Slider>
      </div>

      {/* TESTIMONIALS — hidden for now per request; keep markup so we
          can re-enable later without rewriting the section. */}
      {false && (
        <div className={styles.testimonials}>
          <div className={styles.testimonial}>
            <img src="/assets/testimonial-img-1.svg" alt="testimonial-pic" />
            <div className={styles.rating}>
              <img src="/assets/star-icon-1.svg" alt="star-icon" />
              <img src="/assets/star-icon-1.svg" alt="star-icon" />
              <img src="/assets/star-icon-1.svg" alt="star-icon" />
              <img src="/assets/star-icon-1.svg" alt="star-icon" />
              <img src="/assets/star-icon-1.svg" alt="star-icon" />
            </div>
            <p>
              האפליקציה נוחה לשימוש, הלקוחות קובעות לעצמן וחוסכות לי את כאבי ראש,
              אפשרי לשלוח הודעה יום לפני ללקוחות. אהבתי ממש. ממליצה בחום!
            </p>
            <hr />
            <small>יסכה גבות</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default About;
